import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { COLOR_PRIMARY } from "../../styles/colors";
import { saveDesign, getSavedDesigns, getCurrentDesignName } from "../../store";
import { trackEvent, GA_EVENTS } from "../../utils/analytics";

interface SaveDesignModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful save so the parent can update its label. */
  onSaved: (name: string) => void;
}

const SaveDesignModal: React.FC<SaveDesignModalProps> = ({ open, onClose, onSaved }) => {
  const [name, setName] = useState("");
  const [showOverwriteWarning, setShowOverwriteWarning] = useState(false);

  useEffect(() => {
    if (open) {
      // Pre-fill with current design name if editing a named design
      const current = getCurrentDesignName();
      setName(current || "");
      setShowOverwriteWarning(false);
    }
  }, [open]);

  const existingDesigns = getSavedDesigns();

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    // If name already exists and we haven't confirmed overwrite yet
    if (existingDesigns.includes(trimmed) && trimmed !== getCurrentDesignName() && !showOverwriteWarning) {
      setShowOverwriteWarning(true);
      return;
    }

    saveDesign(trimmed);
    trackEvent(GA_EVENTS.DESIGN_SAVED, { design_name: trimmed });
    onSaved(trimmed);
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSave();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="save-design-dialog-title"
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle
        id="save-design-dialog-title"
        sx={{ fontWeight: 700, color: COLOR_PRIMARY }}
      >
        Save Design
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
          Enter a name for your design. It will be saved to your browser's local storage.
        </Typography>
        <TextField
          autoFocus
          fullWidth
          label="Design Name"
          variant="outlined"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setShowOverwriteWarning(false);
          }}
          onKeyDown={handleKeyDown}
          error={name.trim().length === 0 && name.length > 0}
          helperText={
            name.trim().length === 0 && name.length > 0
              ? "Name cannot be blank"
              : undefined
          }
          inputProps={{ "data-testid": "design-name-input" }}
          sx={{ mt: 1 }}
        />
        {showOverwriteWarning && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            A design named <strong>"{name.trim()}"</strong> already exists. Click
            Save again to overwrite it.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={name.trim().length === 0}
          sx={{
            backgroundColor: COLOR_PRIMARY,
            "&:hover": { backgroundColor: "#0d4e65" },
          }}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveDesignModal;
